import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

const MOTTestItem = ({ test, formatDate, index }) => {
  const [showAdvisoryInfo, setShowAdvisoryInfo] = useState(false);
  
  const formatMileage = (value, unit) => {
    if (!value) return 'Not recorded';
    return `${parseInt(value).toLocaleString()} ${unit?.toLowerCase() || 'miles'}`;
  };

  const getTestResultClass = (result) => {
    switch (result?.toUpperCase()) {
      case 'PASSED':
      case 'PASS':
        return 'text-green-700 font-bold text-2xl';
      case 'FAILED':
      case 'FAIL':
        return 'text-red-700 font-bold text-2xl';
      default:
        return 'text-gray-700 font-bold text-2xl';
    }
  };

  return (
    <div className="border-t border-gray-300 pt-6 mt-6">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-gray-600 text-sm mb-1">Date tested</div>
          <div className="font-bold">{formatDate(test.completedDate)}</div>
          <div className={getTestResultClass(test.testResult)}>
            {test.testResult === 'PASSED' ? 'PASS' : test.testResult === 'FAILED' ? 'FAIL' : test.testResult}
          </div>
        </div>
        <div>
          <div className="text-gray-600 text-sm mb-1">Mileage</div>
          <div className="font-bold">{formatMileage(test.odometerValue, test.odometerUnit)}</div>
          {test.location && (
            <>
              <div className="text-gray-600 text-sm mt-3 mb-1">Test location</div>
              <button className="text-blue-600 underline hover:text-blue-800 flex items-center">
                <ChevronRight className="h-4 w-4 mr-1" />
                View test location
              </button>
            </>
          )}
        </div>
        <div>
          <div className="text-gray-600 text-sm mb-1">MOT test number</div>
          <div className="font-bold">{test.motTestNumber || 'Not available'}</div>
          {test.expiryDate && (
            <>
              <div className="text-gray-600 text-sm mt-3 mb-1">Expiry date</div>
              <div className="font-bold">{formatDate(test.expiryDate)}</div>
            </>
          )}
        </div>
      </div>

      {/* Defects and Advisories */}
      {test.defects && test.defects.length > 0 && (
        <div className="mt-4">
          {/* Dangerous Defects */}
          {test.defects.filter(d => d.type === 'DANGEROUS' || d.dangerous).length > 0 && (
            <div className="mb-4">
              <div className="text-gray-700 mb-2">Repair immediately (dangerous defects):</div>
              <ul className="list-disc pl-5">
                {test.defects.filter(d => d.type === 'DANGEROUS' || d.dangerous).map((defect, i) => (
                  <li key={i} className="mb-1 text-gray-900">{defect.text}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Major Defects */}
          {test.defects.filter(d => d.type === 'MAJOR').length > 0 && (
            <div className="mb-4">
              <div className="text-gray-700 mb-2">Repair immediately (major defects):</div>
              <ul className="list-disc pl-5">
                {test.defects.filter(d => d.type === 'MAJOR').map((defect, i) => (
                  <li key={i} className="mb-1 text-gray-900">{defect.text}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Minor Defects */}
          {test.defects.filter(d => d.type === 'MINOR').length > 0 && (
            <div className="mb-4">
              <div className="text-gray-700 mb-2">Repair as soon as possible (minor defects):</div>
              <ul className="list-disc pl-5">
                {test.defects.filter(d => d.type === 'MINOR').map((defect, i) => (
                  <li key={i} className="mb-1 text-gray-900">{defect.text}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Advisories */}
          {test.defects.filter(d => d.type === 'ADVISORY').length > 0 && (
            <div className="mb-4">
              <div className="text-gray-700 mb-2">Monitor and repair if necessary (advisories):</div>
              <ul className="list-disc pl-5">
                {test.defects.filter(d => d.type === 'ADVISORY').map((defect, i) => (
                  <li key={i} className="mb-1 text-gray-900">{defect.text}</li>
                ))}
              </ul>
              <button 
                onClick={() => setShowAdvisoryInfo(!showAdvisoryInfo)}
                className="text-blue-600 underline hover:text-blue-800 flex items-center mt-2"
              >
                <ChevronRight className="h-4 w-4 mr-1" />
                What are advisories?
              </button>
              
              {showAdvisoryInfo && (
                <div className="mt-3 p-4 bg-gray-50 border-l-4 border-gray-400">
                  <p className="text-gray-700">
                    Advisories are given for guidance. Some of these may need to be monitored 
                    in case they become more serious and need immediate repairs.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MOTTestItem;
